import { useState, useEffect, useCallback, useRef } from 'react';
import { selfServiceAPI } from '../lib/api';
import {
  User, CalendarDays, Shield, Clock, ArrowLeft, Loader2,
  Send, CheckCircle2, AlertCircle, Briefcase, CreditCard,
  Users as UsersIcon, HardHat, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const LEAVE_TYPES = ['Annual Leave', 'Sick Leave', 'Family Responsibility Leave', 'Unpaid Leave', 'Other'];

const AUTO_LOGOUT_MS = 5 * 60 * 1000; // 5 minutes of inactivity

export default function KioskSelfService({ employee, pin, onExit, initialTab = 'profile' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [profile, setProfile] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [ppe, setPpe] = useState([]);
  const [attendance, setAttendance] = useState({ records: [], is_hourly_paid: false });
  const [loading, setLoading] = useState(true);

  // Leave form state
  const [submittingLeave, setSubmittingLeave] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    leave_type: 'Annual Leave',
    start_date: '',
    end_date: '',
    reason: '',
  });

  const empId = employee?.employee_id;

  // Stash onExit in a ref so the data-fetch and auto-logout effects do NOT
  // re-run every time the parent Kiosk re-renders (the kiosk clock ticks
  // every second, which was previously re-firing fetchAll and the inactivity
  // timer once per second — causing the visible "page refreshes every second").
  const onExitRef = useRef(onExit);
  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);

  // Fetch all data in parallel
  const fetchAll = useCallback(async () => {
    if (!empId || !pin) return;
    setLoading(true);
    try {
      const [p, l, pp, a] = await Promise.all([
        selfServiceAPI.getProfile(empId, pin),
        selfServiceAPI.getLeaveHistory(empId, pin),
        selfServiceAPI.getPpe(empId, pin),
        selfServiceAPI.getAttendance(empId, pin),
      ]);
      setProfile(p.data);
      setLeaves(l.data || []);
      setPpe(pp.data || []);
      setAttendance(a.data || { records: [], is_hourly_paid: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load profile');
      onExitRef.current?.();
    } finally {
      setLoading(false);
    }
  }, [empId, pin]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-logout on inactivity
  useEffect(() => {
    let timer = setTimeout(() => {
      toast.info('Session timed out');
      onExitRef.current?.();
    }, AUTO_LOGOUT_MS);
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        toast.info('Session timed out');
        onExitRef.current?.();
      }, AUTO_LOGOUT_MS);
    };
    window.addEventListener('mousedown', reset);
    window.addEventListener('keydown', reset);
    window.addEventListener('touchstart', reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', reset);
      window.removeEventListener('keydown', reset);
      window.removeEventListener('touchstart', reset);
    };
  }, []);

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    if (!leaveForm.start_date || !leaveForm.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (leaveForm.end_date < leaveForm.start_date) {
      toast.error('End date cannot be before start date');
      return;
    }
    setSubmittingLeave(true);
    try {
      const res = await selfServiceAPI.submitLeave({
        employee_id: empId,
        pin,
        ...leaveForm,
      });
      toast.success(res.data.message || 'Leave submitted');
      setLeaveForm({ leave_type: 'Annual Leave', start_date: '', end_date: '', reason: '' });
      // refresh leave history
      const l = await selfServiceAPI.getLeaveHistory(empId, pin);
      setLeaves(l.data || []);
      setActiveTab('leave');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave');
    } finally {
      setSubmittingLeave(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/95 backdrop-blur-xl">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  const balance = profile.leave_balance || { annual: 0, sick: 0, study: 0, unpaid: 0 };

  return (
    <div
      className="fixed inset-0 z-50 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-auto"
      data-testid="kiosk-self-service-panel"
    >
      {/* Header */}
      <header className="sticky top-0 z-10 bg-slate-800/70 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={onExit}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="self-service-exit-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Done
          </Button>
          <div className="text-center">
            <p className="text-sm text-slate-400">Signed in as</p>
            <p className="text-2xl font-bold text-white">{profile.full_name}</p>
          </div>
          <div className="w-[92px]" />
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto mt-4 flex flex-wrap gap-2 justify-center">
          {[
            { id: 'profile', label: 'My Profile', Icon: User },
            { id: 'leave', label: 'My Leave', Icon: CalendarDays },
            { id: 'submit', label: 'Submit Leave', Icon: Send },
            { id: 'ppe', label: 'My PPE', Icon: Shield },
            { id: 'attendance', label: 'Attendance', Icon: Clock },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={cn(
                'px-5 py-2.5 rounded-xl text-base font-medium transition-all flex items-center gap-2',
                activeTab === id
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                  : 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
              )}
              data-testid={`ss-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {activeTab === 'profile' && <ProfileTab profile={profile} balance={balance} />}
        {activeTab === 'leave' && <LeaveHistoryTab leaves={leaves} balance={balance} />}
        {activeTab === 'submit' && (
          <SubmitLeaveTab
            leaveForm={leaveForm}
            setLeaveForm={setLeaveForm}
            submitting={submittingLeave}
            onSubmit={handleSubmitLeave}
            balance={balance}
          />
        )}
        {activeTab === 'ppe' && <PpeTab ppe={ppe} />}
        {activeTab === 'attendance' && <AttendanceTab attendance={attendance} />}
      </main>
    </div>
  );
}

/* ================= Tabs ================= */

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-orange-400" />
        </div>
        <h3 className="text-xl font-semibold text-white">{title}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">{children}</div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className="text-base text-white break-words">{value || <span className="text-slate-500">—</span>}</p>
    </div>
  );
}

function ProfileTab({ profile, balance }) {
  return (
    <div className="space-y-6">
      {/* Leave balance pill row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BalancePill label="Annual" value={balance.annual} color="emerald" />
        <BalancePill label="Sick" value={balance.sick} color="sky" />
        <BalancePill label="Family Resp." value={balance.study} color="violet" />
        <BalancePill label="Unpaid" value={balance.unpaid} color="slate" />
      </div>

      <Section icon={User} title="Personal Details">
        <Field label="Full Name" value={profile.full_name} />
        <Field label="Email" value={profile.email} />
        <Field label="Phone" value={profile.phone} />
        <Field label="Date of Birth" value={profile.date_of_birth} />
        <Field label="Physical Address" value={profile.physical_address} />
        <Field label="Residential Address" value={profile.residential_address} />
      </Section>

      <Section icon={Briefcase} title="Employment">
        <Field label="Department" value={profile.department} />
        <Field label="Job Title" value={profile.job_title} />
        <Field label="Employment Type" value={profile.employment_type} />
        <Field label="Start Date" value={profile.start_date} />
        <Field label="Line Manager" value={profile.line_manager} />
        <Field label="Status" value={profile.status} />
        {profile.contract_end_date && <Field label="Contract End Date" value={profile.contract_end_date} />}
        {profile.is_hourly_paid && (
          <>
            <Field label="Pay Cycle" value={profile.hourly_pay_cycle} />
            <Field label="Shift Type" value={profile.shift_type} />
          </>
        )}
      </Section>

      <Section icon={UsersIcon} title="Next of Kin">
        <Field label="Name" value={profile.next_of_kin_name} />
        <Field label="Relationship" value={profile.next_of_kin_relationship} />
        <Field label="Phone" value={profile.next_of_kin_phone} />
      </Section>

      <Section icon={CreditCard} title="Bank Account (masked)">
        <Field label="Bank" value={profile.bank_name} />
        <Field label="Account Number" value={profile.bank_account_number_masked} />
        <Field label="Account Type" value={profile.bank_account_type} />
      </Section>

      <div className="text-center text-sm text-slate-500 pt-2">
        Details read-only. Contact HR to update personal or bank information.
      </div>
    </div>
  );
}

function BalancePill({ label, value, color }) {
  const colorMap = {
    emerald: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30 text-emerald-300',
    sky: 'from-sky-500/20 to-sky-500/5 border-sky-500/30 text-sky-300',
    violet: 'from-violet-500/20 to-violet-500/5 border-violet-500/30 text-violet-300',
    slate: 'from-slate-500/20 to-slate-500/5 border-slate-500/30 text-slate-300',
  };
  return (
    <div className={cn('bg-gradient-to-br border rounded-2xl p-4 text-center', colorMap[color])} data-testid={`balance-${label.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-wider opacity-80">{label} Leave</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-70">days left</p>
    </div>
  );
}

function LeaveHistoryTab({ leaves, balance }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BalancePill label="Annual" value={balance.annual} color="emerald" />
        <BalancePill label="Sick" value={balance.sick} color="sky" />
        <BalancePill label="Family Resp." value={balance.study} color="violet" />
        <BalancePill label="Unpaid" value={balance.unpaid} color="slate" />
      </div>

      <div className="bg-slate-800/50 border border-white/5 rounded-2xl overflow-hidden">
        {leaves.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 text-slate-600" />
            <p className="text-lg">No leave requests yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900/50 text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Start</th>
                <th className="px-4 py-3 text-left">End</th>
                <th className="px-4 py-3 text-left">Days</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((l, i) => (
                <tr key={l.id || i} className="border-t border-white/5 text-white text-sm">
                  <td className="px-4 py-3">{l.leave_type}</td>
                  <td className="px-4 py-3">{l.start_date}</td>
                  <td className="px-4 py-3">{l.end_date}</td>
                  <td className="px-4 py-3">{l.days}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={l.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    Pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    Rejected: 'bg-red-500/20 text-red-300 border-red-500/30',
  };
  return (
    <Badge variant="outline" className={cn('text-xs', map[status] || 'bg-slate-700/50 text-slate-300 border-slate-600')}>
      {status || 'Pending'}
    </Badge>
  );
}

function SubmitLeaveTab({ leaveForm, setLeaveForm, submitting, onSubmit, balance }) {
  const balanceKeyMap = {
    'Annual Leave': 'annual', 'Sick Leave': 'sick', 'Family Responsibility Leave': 'study', 'Unpaid Leave': 'unpaid', 'Other': 'unpaid',
  };
  const currentBalance = balance?.[balanceKeyMap[leaveForm.leave_type]] ?? 0;

  return (
    <form onSubmit={onSubmit} className="max-w-2xl mx-auto space-y-6" data-testid="self-service-leave-form">
      <div className="bg-slate-800/50 border border-white/5 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Send className="w-5 h-5 text-orange-400" />
          </div>
          <h3 className="text-xl font-semibold text-white">Submit Leave Request</h3>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Leave Type *</Label>
          <Select
            value={leaveForm.leave_type}
            onValueChange={(v) => setLeaveForm((f) => ({ ...f, leave_type: v }))}
          >
            <SelectTrigger className="h-12 bg-slate-900/50 border-slate-700 text-white" data-testid="leave-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEAVE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-400">Current balance: <span className="text-orange-400 font-semibold">{currentBalance} days</span></p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Start Date *</Label>
            <Input
              type="date"
              value={leaveForm.start_date}
              onChange={(e) => setLeaveForm((f) => ({ ...f, start_date: e.target.value }))}
              className="h-12 bg-slate-900/50 border-slate-700 text-white"
              data-testid="leave-start-date"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">End Date *</Label>
            <Input
              type="date"
              value={leaveForm.end_date}
              onChange={(e) => setLeaveForm((f) => ({ ...f, end_date: e.target.value }))}
              className="h-12 bg-slate-900/50 border-slate-700 text-white"
              data-testid="leave-end-date"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-300">Reason (optional)</Label>
          <Textarea
            value={leaveForm.reason}
            onChange={(e) => setLeaveForm((f) => ({ ...f, reason: e.target.value }))}
            className="bg-slate-900/50 border-slate-700 text-white min-h-[100px]"
            placeholder="e.g. family function, rest day..."
            data-testid="leave-reason"
          />
        </div>

        <div className="flex items-start gap-2 text-sm text-slate-400 bg-slate-900/40 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>Your request will be submitted as <strong className="text-amber-400">Pending</strong> and reviewed by your line manager.</p>
        </div>

        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="w-full h-14 text-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
          data-testid="submit-leave-btn"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5 mr-2" /> Submit Leave Request</>}
        </Button>
      </div>
    </form>
  );
}

function PpeTab({ ppe }) {
  return (
    <div className="bg-slate-800/50 border border-white/5 rounded-2xl overflow-hidden">
      {ppe.length === 0 ? (
        <div className="py-16 text-center text-slate-400">
          <HardHat className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No PPE issued yet</p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-slate-900/50 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3 text-left">Item</th>
              <th className="px-4 py-3 text-left">Size</th>
              <th className="px-4 py-3 text-left">Qty</th>
              <th className="px-4 py-3 text-left">Issued</th>
              <th className="px-4 py-3 text-left">Due</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {ppe.map((p, i) => (
              <tr key={p.id || i} className="border-t border-white/5 text-white text-sm">
                <td className="px-4 py-3">{p.item_name || p.ppe_item_name}</td>
                <td className="px-4 py-3">{p.size || '—'}</td>
                <td className="px-4 py-3">{p.quantity ?? 1}</td>
                <td className="px-4 py-3">{p.issue_date}</td>
                <td className="px-4 py-3">{p.next_due_date || '—'}</td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AttendanceTab({ attendance }) {
  if (!attendance.is_hourly_paid) {
    return (
      <div className="bg-slate-800/50 border border-white/5 rounded-2xl py-16 text-center text-slate-400">
        <Clock className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <p className="text-lg">Attendance tracking is for hourly-paid employees only.</p>
      </div>
    );
  }
  const records = attendance.records || [];
  return (
    <div className="bg-slate-800/50 border border-white/5 rounded-2xl overflow-hidden">
      {records.length === 0 ? (
        <div className="py-16 text-center text-slate-400">
          <Clock className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-lg">No attendance recorded in the last 30 days</p>
        </div>
      ) : (
        <table className="w-full">
          <thead className="bg-slate-900/50 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3 text-left">Shift Date</th>
              <th className="px-4 py-3 text-left">Day</th>
              <th className="px-4 py-3 text-left">Type</th>
              <th className="px-4 py-3 text-left">Clock In</th>
              <th className="px-4 py-3 text-left">Clock Out</th>
              <th className="px-4 py-3 text-left">Day Hours</th>
              <th className="px-4 py-3 text-left">Night Hours</th>
              <th className="px-4 py-3 text-left">Overtime</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={`${r.shift_date}-${r.shift_type || 'day'}`} className="border-t border-white/5 text-white text-sm">
                <td className="px-4 py-3">{r.shift_date}</td>
                <td className="px-4 py-3">{r.day_of_week}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={cn(
                    'text-xs',
                    r.shift_type === 'Night Shift'
                      ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                      : 'bg-sky-500/20 text-sky-300 border-sky-500/30'
                  )}>
                    {r.shift_type || 'Day Shift'}
                  </Badge>
                </td>
                <td className="px-4 py-3">{r.first_clock_in?.slice(0, 5) || '—'}</td>
                <td className="px-4 py-3">
                  {r.last_clock_out?.slice(0, 5) || '—'}
                  {r.last_clock_out_date && r.last_clock_out_date !== r.shift_date && (
                    <span className="ml-1 text-[10px] text-indigo-300">(+1d)</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sky-300">
                  {(r.day_shift_hours ?? 0) > 0 ? `${r.day_shift_hours.toFixed(2)}h` : '—'}
                </td>
                <td className="px-4 py-3 text-indigo-300">
                  {(r.night_shift_hours ?? 0) > 0 ? `${r.night_shift_hours.toFixed(2)}h` : '—'}
                </td>
                <td className="px-4 py-3 text-orange-300">
                  {r.overtime_hours > 0 ? `${r.overtime_hours.toFixed(2)}h` : '—'}
                </td>
                <td className="px-4 py-3">
                  {r.status === 'Complete' ? (
                    <Badge variant="outline" className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Complete
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                      <XCircle className="w-3 h-3 mr-1" /> Incomplete
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
